import React, { useState, useEffect } from 'react';
import MetaTags from 'react-meta-tags';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Button, Row, Col, Card, CardBody } from 'reactstrap';
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
  SizePerPageDropdownStandalone,
} from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';

import '../../assets/scss/datatables.scss';
import { getFirebaseBackend } from '../../helpers/firebaseHelper';
import { normalizeString } from '../../utils/';
import { getDefaultSorted, getPageOptions } from './config/tableConfig';
import { getDeactivatedUserColumns } from './config/userTableColumns';
import EditButton from 'components/Edit-Button';
import { useStatusCustomization } from 'hooks/useStatusCustomization';

const ListDeactivated = () => {
  const firebaseHelper = getFirebaseBackend();
  const statusMap = useStatusCustomization();
  const { t: translate } = useTranslation();
  const { SearchBar } = Search;
  const [users, setUsers] = useState([]);
  const [deactivatedUsers, setDeactivatedUsers] = useState([]);

  const deactivatedTotal = (acc, cur) => (cur.status === 'Deactivated' ? ++acc : acc);
  const activatedTotal = (acc, cur) => (cur.status === 'Activated' ? ++acc : acc);
  const pendingTotal = (acc, cur) => (cur.status === 'Pending' ? ++acc : acc);

  const fetchAllUsers = async () => {
    const usersObj = await firebaseHelper.getAllUsers();
    setUsers(usersObj);
  };

  const processDeactivatedUsers = async () => {
    const processedUsers = await Promise.all(
      Object.values(users)
        .filter((value) => value.status === 'Deactivated')
        .map(async (user) => {
          const accountName = await firebaseHelper.accountName(user.account);

          return {
            id: user.id,
            name: `${user.name} ${user.lastName}`,
            email: user.email,
            account: accountName,
            status: statusMap[normalizeString(user.status)],
            action: <EditButton link={`/edit-user?id=${user.id}`} />,
          };
        }),
    );
    setDeactivatedUsers(processedUsers);
  };

  useEffect(() => {
    if (!users) return;

    processDeactivatedUsers();
  }, [users]);

  useEffect(() => {
    fetchAllUsers();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content" style={{ minHeight: '100vh' }}>
        <MetaTags>
          <title>{translate('List Deactivated Users')} | Ectools</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col xs="12" className="mb-4">
              <h4 className="mb-0 font-size-18">
                {translate('Deactivated Users')} (
                {deactivatedUsers !== null ? Object.values(users).reduce(deactivatedTotal, 0) : 0})
              </h4>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <PaginationProvider
                    pagination={paginationFactory(getPageOptions(deactivatedUsers.length))}
                    keyField="id"
                    columns={getDeactivatedUserColumns(translate)}
                    data={deactivatedUsers}
                  >
                    {({ paginationProps, paginationTableProps }) => (
                      <ToolkitProvider
                        keyField="id"
                        columns={getDeactivatedUserColumns(translate)}
                        data={deactivatedUsers}
                        search
                      >
                        {(toolkitProps) => (
                          <React.Fragment>
                            <Row className="mb-2 d-flex justify-content-between">
                              <Col md="4">
                                <div className="search-box me-2 mb-2 d-inline-block">
                                  <div className="position-relative">
                                    <SearchBar
                                      {...toolkitProps.searchProps}
                                      placeholder={translate('Search')}
                                    />
                                    <i className="bx bx-search-alt search-icon" />
                                  </div>
                                </div>
                              </Col>
                              <Col md="8" className="d-flex flex-row-reverse align-items-center">
                                <Button
                                  color="primary"
                                  className="d-flex gap-2 align-items-center"
                                  tag={Link}
                                  to="/create-user"
                                >
                                  <i className="bx bx-plus-circle" />
                                  {translate('Create new User')}
                                </Button>

                                <Link to="/list-pending">
                                  <p className="mx-4 mb-0" style={{ color: '#ADB5BD' }}>
                                    {translate('Pending Users')} (
                                    {users !== null
                                      ? Object.values(users).reduce(pendingTotal, 0)
                                      : 0}
                                    )
                                  </p>
                                </Link>
                                <Link to="/list-users">
                                  <p className="mx-4 mb-0" style={{ color: '#ADB5BD' }}>
                                    {translate('See all users')} (
                                    {users !== null
                                      ? Object.values(users).reduce(activatedTotal, 0)
                                      : 0}
                                    )
                                  </p>
                                </Link>
                              </Col>
                            </Row>

                            <Row>
                              <Col xl="12">
                                <div className="table-responsive">
                                  <BootstrapTable
                                    keyField={'id'}
                                    responsive
                                    hover
                                    bordered={false}
                                    striped={false}
                                    defaultSorted={getDefaultSorted}
                                    classes={'table align-middle table-nowrap'}
                                    headerWrapperClasses={'thead-light'}
                                    {...toolkitProps.baseProps}
                                    {...paginationTableProps}
                                  />
                                </div>
                              </Col>
                            </Row>

                            <Row className="align-items-md-center mt-30">
                              <Col className="inner-custom-pagination d-flex">
                                <div className="d-inline">
                                  <SizePerPageDropdownStandalone {...paginationProps} />
                                </div>
                                <div className="text-md-right ms-auto">
                                  <PaginationListStandalone {...paginationProps} />
                                </div>
                              </Col>
                            </Row>
                          </React.Fragment>
                        )}
                      </ToolkitProvider>
                    )}
                  </PaginationProvider>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

ListDeactivated.displayName = 'List Deactivated Users Page';

export default ListDeactivated;
